// lib/moderacja/historia.ts — HISTORIA MODERACJI (zakładka „Historia"): rozstrzygnięcia, filtry, decyzje.
//
// Przeniesione 1:1 Z FUNKCJI ze starego panelu (`_helpers/historia-moderacji.ts`). Wartości
// `resolution` i filtry to słownik systemu głównego — bez nowych statusów.
import type { FiltrHistoriiModeracji } from '@/lib/api/analytics';

export const FILTRY_HISTORII: readonly { klucz: FiltrHistoriiModeracji; etykieta: string }[] = [
  { klucz: 'all', etykieta: 'Wszystkie rozstrzygnięte' },
  { klucz: 'auto', etykieta: 'Zatwierdzone automatycznie (AI)' },
  { klucz: 'operator', etykieta: 'Decyzje operatora' },
  { klucz: 'approved', etykieta: 'Zatwierdzone (AI + operator)' },
  { klucz: 'rejected', etykieta: 'Odrzucone' },
  { klucz: 'superseded', etykieta: 'Zastąpione nowszą propozycją' },
];

export function etykietaRozstrzygniecia(resolution: string | null | undefined): string {
  switch (resolution) {
    case 'AUTO_APPROVED': return 'AUTO_APPROVED — zatwierdzone przez AI';
    case 'AUTO_REJECTED': return 'AUTO_REJECTED — odrzucone przez AI';
    case 'OPERATOR_APPROVED': return 'APPROVED — zatwierdzone przez operatora';
    case 'OPERATOR_REJECTED': return 'REJECTED — odrzucone przez operatora';
    case 'SUPERSEDED': return 'SUPERSEDED — zastąpione nowszą propozycją';
    case 'SHADOW': return 'SHADOW — obserwacja (bez decyzji)';
    case 'APPROVED': return 'APPROVED — zatwierdzone (inny kanał)';
    case 'REJECTED': return 'REJECTED — odrzucone (inny kanał)';
    case 'APPLIED_EXTERNALLY': return 'APPLIED_EXTERNALLY — zastosowano wcześniej przez legacy approval (operator nieznany)';
    case 'REJECTED_EXTERNALLY': return 'REJECTED_EXTERNALLY — odrzucono w zakładce „Do potwierdzenia” (operator nieznany)';
    case 'MEAL_DELETED': return 'MEAL_DELETED — danie usunięte, propozycja bezprzedmiotowa';
    default: return resolution || '—';
  }
}

function dataPl(d: Date): string {
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

/**
 * Rekoncyliacja historyczna: `resolvedBy` niesie sufiks `…:reconciled@<UTC>;src=<…>;conf=<…>`. Data
 * rozstrzygnięcia jest wtedy ODZYSKANA albo OSZACOWANA — nigdy przedstawiana jako pewny czas audytu.
 * Bez sufiksu — pusty łańcuch.
 */
export function opisRekoncyliacji(resolvedBy: string | null | undefined): string {
  if (!resolvedBy) return '';
  const m = /reconciled@([^;\s]+)/.exec(resolvedBy);
  if (!m) return '';
  const data = new Date(m[1]!);
  const kiedy = Number.isNaN(data.getTime()) ? m[1] : dataPl(data);
  const zrodlo = /(?:^|;)src=([^;]+)/.exec(resolvedBy)?.[1] ?? '';
  const pewnosc = /(?:^|;)conf=([^;]+)/.exec(resolvedBy)?.[1] ?? '';
  let podstawa = 'pierwotny czas zdarzenia nie jest dostępny — data rozstrzygnięcia = czas rekoncyliacji';
  if (zrodlo === 'proxy-log') podstawa = 'data rozstrzygnięcia = ODZYSKANY czas historycznego zdarzenia z logu proxy';
  else if (zrodlo === 'meals-updatedat') podstawa = 'data rozstrzygnięcia = OSZACOWANY czas historycznego zdarzenia (ostatni zapis dania)';
  return `Zrekoncyliowano ${kiedy} (${podstawa}${pewnosc ? `; pewność ${pewnosc}` : ''})`;
}

export function etykietaDecyzjiAi(aiDecision: string | null | undefined): string {
  if (aiDecision === 'AutoApprove') return 'AI: zatwierdź';
  if (aiDecision === 'ManualReview') return 'AI: do ręcznego przeglądu';
  if (aiDecision === 'Reject') return 'AI: odrzuć';
  return 'AI: brak śladu';
}

export function skrotOperatora(operatorId: string | null | undefined): string {
  if (!operatorId) return '?';
  return operatorId.length > 8 ? `${operatorId.slice(0, 8)}…` : operatorId;
}

function zRekoncyliacja(tekst: string, resolvedBy: string | null | undefined): string {
  const r = opisRekoncyliacji(resolvedBy);
  return r ? `${tekst}. ${r}.` : tekst;
}

export function decyzjaOperatora(wpis: { resolution: string | null; operatorId: string | null; resolvedBy: string | null }): string {
  switch (wpis.resolution) {
    case 'OPERATOR_APPROVED': return `Zatwierdzono (operator ${skrotOperatora(wpis.operatorId)})`;
    case 'OPERATOR_REJECTED': return `Odrzucono (operator ${skrotOperatora(wpis.operatorId)})`;
    case 'AUTO_APPROVED': return 'brak — decyzja automatu';
    case 'SUPERSEDED': return 'brak — zastąpiona nowszą propozycją';
    case 'APPLIED_EXTERNALLY': return zRekoncyliacja('Zastosowano wcześniej przez legacy approval (operator nieznany)', wpis.resolvedBy);
    case 'REJECTED_EXTERNALLY': return zRekoncyliacja('Odrzucono w zakładce „Do potwierdzenia” (operator nieznany)', wpis.resolvedBy);
    case 'MEAL_DELETED': return zRekoncyliacja('brak — danie usunięte, propozycja bezprzedmiotowa', wpis.resolvedBy);
    default: return wpis.resolvedBy || '—';
  }
}

/** Liczba stron (0 wyników = 1 pusta strona). */
export function liczbaStron(wszystkich: number, rozmiar: number): number {
  if (!wszystkich || wszystkich <= 0 || !rozmiar || rozmiar <= 0) return 1;
  return Math.ceil(wszystkich / rozmiar);
}

/** Tekst pod tabelą: „1–20 z 47". */
export function zakresWynikow(strona: number, rozmiar: number, wszystkich: number): string {
  if (!wszystkich) return '0 z 0';
  const od = (strona - 1) * rozmiar + 1;
  const doN = Math.min(strona * rozmiar, wszystkich);
  return `${od}–${doN} z ${wszystkich}`;
}
